import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, User, Eye, EyeOff, LogIn, Home } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, credentials);
      
      if (response.data.status === 'success') {
        localStorage.setItem('admin_logged_in', 'true');
        localStorage.setItem('admin_username', credentials.username);
        toast.success('Welcome back!');
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 100);
      }
    } catch (error) {
      console.error('Login failed:', error);
      if (error.response?.status === 401) {
        toast.error('Invalid username or password');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A0505] flex items-center justify-center px-4">
      {/* Back to Home Button */}
      <Button
        onClick={() => navigate('/')}
        variant="ghost"
        className="absolute top-4 left-4 text-gold hover:text-white hover:bg-white/10"
        data-testid="back-to-home"
      >
        <Home className="w-5 h-5 mr-2" />
        Back to Home
      </Button>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-4">
            <span className="text-gold font-accent text-4xl">ॐ</span>
          </div>
          <h1 className="text-3xl font-heading font-bold text-gold mb-2">Indicraft</h1>
          <p className="text-gold/60 font-accent tracking-widest text-sm">ADMIN PANEL</p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-gold/10">
          <h2 className="text-xl font-heading font-semibold text-white mb-6 text-center">
            Sign In to Dashboard
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gold/70 mb-2">Username</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gold/40" />
                <Input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="Enter username"
                  className="h-12 pl-12 bg-white/5 border-gold/20 text-white placeholder:text-gold/30 focus:border-gold"
                  required
                  data-testid="username-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gold/70 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gold/40" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter password"
                  className="h-12 pl-12 pr-12 bg-white/5 border-gold/20 text-white placeholder:text-gold/30 focus:border-gold"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gold/40 hover:text-gold"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gold hover:bg-gold-dark text-maroon-dark font-semibold rounded-xl transition-all"
              data-testid="login-button"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-maroon-dark/30 border-t-maroon-dark rounded-full animate-spin" />
                  Signing in...
                </div>
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-gold/30 text-sm mt-6">
          © 2026 Indicraft. Sacred Puja Essentials.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
